import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { FilterConfigType, TableColumn, TableComponent } from "@shared/components/table/table.component";
import { LazyLoadEvent, MessageService, SharedModule } from "primeng/api";
import { ApiService, Country, Region, UserRole } from "@core/services/api/api.service";
import { LoaderService } from "@core/services/gigya/loader/loader.service";
import { finalize } from "rxjs";
import { AuditLogs, AuditPermissionService, Role, Users } from "@core/services/audit-permission.service";
import { ModuleTitleComponent } from "@shared/components/module-title/module-title.component";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { saveAs } from 'file-saver';

export const Level = {
  LEVEL1: "l1",
  LEVEL2: "l2",
  LEVEL3: "l3",
} as const;

export const RoleBaseApiCall = {
  LOCAL_RA: "Local RA (Bayer RA colleague)",
  RA_ADMIN: "RA Admin",
  GLOBAL_RA: "Global RA",
};

@Component({
  selector: "app-audit-logs",
  templateUrl: "./audit-logs.component.html",
  styleUrl: "./audit-logs.component.scss",
  imports: [CommonModule, TableComponent, SharedModule, ModuleTitleComponent],
  providers: [MessageService],
})
export class AuditLogsComponent implements OnInit {
  auditData: AuditLogs[] = [];
  auditColumns: TableColumn[] = [];
  auditGlobalFilterFields: string[] = [];
  auditFiltersConfig: FilterConfigType[] = [];
  auditFiltersValue: { [key: string]: any } = {};

  auditActions: any[] = [];

  auditTotalRecords: number = 0;
  auditLoading: boolean = false;
  auditPage: number = 1;
  auditPageSize: number = 10;
  auditLastLazyLoadEvent: LazyLoadEvent | null = null;
  filterRegions: Region[] = [];
  filterUserRoles: UserRole[] = [];
  filterCountries: Country[] = [];
  filterUser: Users[] = [];
  filterAction: Role[] = [];
  roleBaseApiCall: any = Level.LEVEL1;
  // Click popup state for +N
  listPopupVisible = false;
  listPopupTitle = "Items";
  listPopupItems: string[] = [];
  listPopupX = 0;
  listPopupY = 0;
  statusFilter: any;
  isExporting: boolean = false;
  
  constructor(
    private cdr: ChangeDetectorRef, 
    private loaderService: LoaderService, 
    private apiService: ApiService, 
    private AuditPermissionService: AuditPermissionService,
    private messageService: MessageService
  ) {}
  ngOnInit(): void {
    const profile = JSON.parse(localStorage.getItem("profile") || "{}");
    this.setUserLevel(profile);
    this.fetchFilterOptions();
    this.setTabConfig();
    this.updateFilterOptions();
    this.loadOnboardedAuditsLogs();

    this.initializeFilterValues(this.auditFiltersConfig, this.auditFiltersValue);
  }

  setUserLevel(profile: any) {
    if (RoleBaseApiCall.GLOBAL_RA === profile.userRole) {
      this.roleBaseApiCall = Level.LEVEL2;
    } else if (RoleBaseApiCall.RA_ADMIN === profile.userRole) {
      this.roleBaseApiCall = Level.LEVEL3;
    } else {
      this.roleBaseApiCall = Level.LEVEL1;
    }
  }

  initializeFilterValues(config: FilterConfigType[], values: { [key: string]: any }) {
    config.forEach((f) => {
      if (f.type === "multiselect") {
        values[f.key] = [];
      } else if (f.type === "daterange") {
        values[f.key] = { from: null, to: null };
      } else {
        values[f.key] = null;
      }
    });
  }
  onLazyLoadEventFromTable(event: LazyLoadEvent): void {
    this.auditLastLazyLoadEvent = event;
    this.loadOnboardedAuditsLogs(event);
  }
  setTabConfig(): void {
    this.auditColumns = [
      { field: "sno", header: "S.No", sortable: false, width: "2rem" },
      { field: "user_name", header: "Name", sortable: false },
      { field: "email", header: "Email ID", sortable: false, truncateAt: 18 },
      {
        field: "region_name",
        header: "Responsible Region",
        sortable: false,
        filterable: false,
      },
      {
        field: "formatted_user_responsible_country",
        header: "Responsible Country",
        sortable: false,
        isCustom: true,
        customTemplate: (rowData: any) => {
          const first = rowData.responsible_country_first || "N/A";
          const remain = Number(rowData.responsible_country_remaining || 0);
          const others: string[] = Array.isArray(rowData.responsible_country_remaining_names) ? rowData.responsible_country_remaining_names : [];
          const encoded = encodeURIComponent(JSON.stringify(others));
          return `  <div class="cell-multi">
            <span class="primary-text">${this.escapeHtml(String(first))}</span>
            ${
              remain
                ? `<span role="button" tabindex="0" class="more-badge white-outline country-count-badge--outlined">+${remain}</span><span class="more-items" style="display:none">${JSON.stringify(
                    others
                  )}</span>`
                : ""
            }
          </div>`;
        },
      },
      { field: "role_type", header: "Role Type", sortable: false, truncateAt: 17 },
      {
        field: "timestamp",
        header: "Timestamp",
        sortable: false,
      },
      {
        field: "action",
        header: "Action",
        sortable: false,
      },
      {
        field: "target_name",
        header: "Target Name",
        sortable: false,
        truncateAt: 18,
      },
      {
        field: "description",
        header: "Description",
        sortable: false,
        truncateAt: 18,
      },
      { field: "target_status", header: "Status", sortable: false },
    ];
    this.auditGlobalFilterFields = ["user_name", "email", "status"];
  }
  updateFilterOptions(): void {
    this.auditFiltersConfig = [
      {
        key: "date_range",
        label: "Date Range",
        type: "daterange",
      },
      {
        key: "role_type",
        label: "Role Type",
        type: "multiselect",
        options: this.filterUserRoles.map((r) => ({
          label: r.name,
          value: String(r.id),
        })),
      },
      {
        key: "region_id",
        label: "Responsible Region",
        type: "multiselect",
        options: this.filterRegions.map((r) => ({
          label: r.name,
          value: String(r.id),
        })),
      },

      {
        key: "country_id",
        label: "Responsible Country",
        type: "multiselect",
        options: this.filterCountries.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      },
      {
        key: "user_ids",
        label: "User",
        type: "multiselect",
        options: this.filterUser.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      },
      {
        key: "action",
        label: "Action",
        type: "multiselect",
        options: this.filterAction.map((c) => ({
          label: c.label,
          value: c.value,
        })),
      },
      {
        key: "statuses",
        label: "Status",
        type: "multiselect",
        options: this.statusFilter?.map((c: any) => ({
          label: c.label,
          value: c.value,
        })),
      },
    ];
    this.cdr.detectChanges();
  }
  fetchFilterOptions(): void {
    this.getDropdownOption();
    this.apiService.getRegions().subscribe({
      next: (response) => {
        this.filterRegions = response.regions;
        this.updateFilterOptions();
      },
      error: (err) => {},
    });
    this.apiService.getUserRoles().subscribe({
      next: (response) => {
        this.filterUserRoles = response.user_roles;
        this.updateFilterOptions();
      },
      error: (err) => {},
    });
    this.apiService.getCountries().subscribe({
      next: (response) => {
        if (response && response.countries) {
          this.filterCountries = response.countries;
          this.updateFilterOptions();
        }
      },
      error: (err) => {},
    });
  }
  getDropdownOption() {
    this.AuditPermissionService.getDropdownAuditFIlterLogs(this.roleBaseApiCall).subscribe({
      next: (data) => {
        this.filterAction = data.actions.map((ele: any) => {
          return {
            label: this.convertEnumToLabel(ele),
            value: ele,
          };
        });
        this.statusFilter = data.statuses.map((ele: any) => {
          return {
            label: this.convertEnumToLabel(ele),
            value: ele,
          };
        });
        this.filterUser = data.users;
        this.updateFilterOptions();
      },
      error: (error) => {},
    });
  }

  getCountryNames(countryIds: number[]): string[] {
    if (!countryIds || countryIds.length === 0) {
      return ["N/A"];
    }
    const countryNames = countryIds
      .map((id) => {
        const country = this.filterCountries.find((c) => c.id === id);
        return country ? country.name : "";
      })
      .filter((name) => name !== "");
    if (countryNames.length === 0) {
      return ["N/A"];
    }
    return countryNames;
  }
  getRoleName(roleId: number): string {
    const role = this.filterUserRoles.find((r) => r.id === roleId);
    return role ? role.name : "N/A";
  }

  getRegionName(regionId: number): string {
    const region = this.filterRegions.find((r) => r.id === regionId);
    return region ? region.name : "N/A";
  }

  onAuditSearchClicked(filters: { [key: string]: any }) {
    const processedFilters: { [key: string]: any } = {};
    for (const key in filters) {
      if (filters.hasOwnProperty(key)) {
        let value = filters[key];

        // Handle multiselect values: ensure all elements are primitive values
        if (Array.isArray(value)) {
          // Filter out any non-object items first, then map
          const mappedValues = value
            .map((item) => {
              if (typeof item === "object" && item !== null && "value" in item) {
                return item.value; // Extract 'value' from {label, value} object
              }
              return item; // Keep primitive values as is
            })
            .filter((item) => item !== null && item !== undefined); // Remove any null/undefined from mapping

          processedFilters[key] = mappedValues;
        }
        // Handle daterange objects
        else if (typeof value === "object" && value !== null && "from" in value && "to" in value) {
          processedFilters[key] = value;
        }
        // Handle other non-empty values
        else if (value !== null && value !== undefined && value !== "") {
          processedFilters[key] = value;
        } else {
          // If value is empty string, null, or undefined, ensure it's not included
          processedFilters[key] = null; // Or you can choose to not include the key at all
        }
      }
    }

    // Clean up processedFilters to remove nulls/empty arrays if desired for cleaner state
    for (const key in processedFilters) {
      if (processedFilters.hasOwnProperty(key)) {
        const value = processedFilters[key];
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          delete processedFilters[key];
        }
      }
    }
    const currentGlobalFilter = this.auditLastLazyLoadEvent?.globalFilter;
    this.auditLastLazyLoadEvent = {
      first: 0,
      rows: this.auditPageSize,
      globalFilter: currentGlobalFilter,
    };
    this.loadOnboardedAuditsLogs(this.auditLastLazyLoadEvent);
  }

  loadOnboardedAuditsLogs(event?: LazyLoadEvent): void {
    this.auditLoading = true;
    this.loaderService.show();

    const currentEvent = event || this.auditLastLazyLoadEvent || { first: 0, rows: this.auditPageSize };

    this.auditPage = (currentEvent.first ?? 0) / (currentEvent.rows ?? this.auditPageSize) + 1;
    this.auditPageSize = currentEvent.rows ?? this.auditPageSize;

    const filtersToSend: { [key: string]: any } = { ...this.auditFiltersValue };

    if (currentEvent.globalFilter && currentEvent.globalFilter.trim() !== "") {
      filtersToSend["search"] = currentEvent.globalFilter.trim();
    }
    this.AuditPermissionService.getAuditLogs(this.roleBaseApiCall, this.auditPage, this.auditPageSize, this.formatFilters(filtersToSend))
      .pipe(
        finalize(() => {
          this.auditLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.auditData = response.logs.map((log: AuditLogs, index: any) => ({
            ...log,
            sno: (this.auditPage - 1) * this.auditPageSize + index + 1,
            ...log.user_info,
            country_name: this.getCountryNames(log.user_info.responsible_countries),
            //        const firstName: string = assignedNames[0] || 'N/A';
            // const remainingCount: number = Math.max(0, assignedNames.length - 1);
            // const remainingNames: string[] = remainingCount > 0 ? assignedNames.slice(1) : [];
            formatted_user_responsible_country: this.getCountryNames(log.user_info.responsible_countries)[0],
            responsible_country_first: this.getCountryNames(log.user_info.responsible_countries)[0],
            responsible_country_remaining: Math.max(0, this.getCountryNames(log.user_info.responsible_countries).length - 1),
            responsible_country_remaining_names:
              Math.max(0, this.getCountryNames(log.user_info.responsible_countries).length - 1) > 0 ? this.getCountryNames(log.user_info.responsible_countries).slice(1) : [],
            role_type: this.getRoleName(log.user_info.role_id),
            region_name: this.getRegionName(log.user_info.region_id),
            action: this.convertEnumToLabel(log.action),
            timestamp: this.convertTimestampToDate(log.timestamp),
          }));
          this.auditTotalRecords = response?.meta.total;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.auditData = [];
          this.auditTotalRecords = 0;
        },
      });
  }
 convertTimestampToDate(timestamp: string | number | Date) {
  const date = new Date(timestamp);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const formattedDay = day < 10 ? "0" + day : day;
  const formattedMonth = month < 10 ? "0" + month : month;
  const formattedHours = hours < 10 ? "0" + hours : hours;
  const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
  const formattedSeconds = seconds < 10 ? "0" + seconds : seconds;

  return `${formattedDay}/${formattedMonth}/${year},${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

  formatFilters(filters: { [key: string]: any }): {
    [key: string]: string;
  } {
    const formattedFilters: { [key: string]: string } = {};

    // Key mapping from frontend to Swagger API
    const keyMapping: { [key: string]: string } = {
      role_type: "role_ids",
      region_id: "region_ids",
      country_id: "country_ids",
      action: "actions",
      status: "statuses",
    };

    if (filters["date_range"] && Array.isArray(filters["date_range"])) {
      const dateParts = filters["date_range"];
      if (dateParts.length >= 2) {
        const startDate = new Date(dateParts[0]);
        const endDate = new Date(dateParts[1]);
        if (!isNaN(startDate.getTime())) {
          formattedFilters["date_from"] = this.onDateSelect(startDate);
        }
        if (!isNaN(endDate.getTime())) {
          formattedFilters["date_to"] = this.onDateSelect(endDate);
        }
      }
    }

    // Process other filters
    for (const key in filters) {
      if (filters.hasOwnProperty(key) && key !== "date_range") {
        const value = filters[key];

        if (value === null || value === undefined || value === "") {
          continue;
        }
        const apiKey = keyMapping[key] || key;
        if (Array.isArray(value) && value.length > 0) {
          const extractedValues = value
            .map((item) => {
              if (typeof item === "object" && item !== null && "value" in item) {
                return item.value;
              }
              return item;
            })
            .filter((v) => v !== null && v !== undefined);
          if (extractedValues.length > 0) {
            formattedFilters[apiKey] = extractedValues.join(",");
          }
        } else if (typeof value === "object" && value !== null) {
        } else {
          formattedFilters[apiKey] = String(value);
        }
      }
    }

    return formattedFilters;
  }
   onDateSelect(event: Date) {
    const year = event.getFullYear();
    const month = String(event.getMonth() + 1).padStart(2, "0");
    const day = String(event.getDate()).padStart(2, "0");
    const onlyDate = `${year}-${month}-${day}`;
    return onlyDate;
  }

  convertEnumToLabel(value: string): string {
    return value
      .toLowerCase()
      .split("_")
      .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ");
  }
  private escapeHtml(v: string): string {
    return v.replace(
      /[&<>"']/g,
      (c) =>
        ((
          {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          } as any
        )[c])
    );
  }
  @HostListener("document:click", ["$event"])
  onDocumentClick(ev: MouseEvent): void {
    const target = ev.target as EventTarget | null;
    const badgeEl = target && (target as Element).closest ? (target as Element).closest(".more-badge") : null;
    if (badgeEl instanceof HTMLElement) {
      const container = badgeEl.closest(".cell-multi");
      const hidden = container?.querySelector(".more-items");
      let list: string[] = [];
      const rawText = hidden?.textContent?.trim();
      if (rawText) {
        try {
          list = JSON.parse(rawText);
        } catch {}
      }
      // if (!list.length) return;
      const titleEl = badgeEl.getAttribute("data-title");
      const title = titleEl || "Countries";
      const rect = badgeEl.getBoundingClientRect();
      this.openListPopup({ title, items: list, x: rect.left, y: rect.bottom });
      return;
    }

    // Close when clicking outside the popup
    const popup = document.querySelector(".list-popup");
    if (popup && target instanceof Node && !popup.contains(target)) {
      this.closeListPopup();
    }
  }
  openListPopup(payload: { title: string; items: string[]; x?: number; y?: number }): void {
    this.listPopupTitle = payload.title;
    this.listPopupItems = payload.items;
    this.listPopupX = Math.max(12, Math.min(payload.x ?? 0, window.innerWidth - 280));
    this.listPopupY = Math.max(12, Math.min(payload.y ?? 0, window.innerHeight - 280));
    this.listPopupVisible = true;
  }

  closeListPopup(): void {
    this.listPopupVisible = false;
  }

  handleExport(): void {
    this.isExporting = true; // Disable export button
    const exportFilters = this.formatFilters(this.auditFiltersValue);
    
    // Add global search term if it exists from lazy load event
    if (this.auditLastLazyLoadEvent?.globalFilter && this.auditLastLazyLoadEvent.globalFilter.trim() !== "") {
      exportFilters["search"] = this.auditLastLazyLoadEvent.globalFilter.trim();
    }
    
    this.AuditPermissionService.exportAuditLogs(
      this.roleBaseApiCall,
      this.auditPage,
      this.auditPageSize,
      exportFilters
    )
      .pipe(
        finalize(() => {
          this.isExporting = false; // Re-enable export button
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const blob = response.body;
          if (!blob) {
            this.messageService.add({
              severity: "error",
              summary: "Export Failed",
              detail: "No file data received from the server.",
              life: 5000,
            });
            return;
          }
          let filename = `Audit_Logs_Export.xlsx`; // Default filename
          // Attempt to extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\n]*?)['"]?;?/i);
            if (filenameMatch && filenameMatch[1]) {
              try {
                // Decode URI component to handle potential URL-encoded characters
                filename = decodeURIComponent(filenameMatch[1].replace(/^UTF-8''/, ''));
              } catch (e) {
                console.warn('Could not decode filename from Content-Disposition, using raw:', filenameMatch[1]);
                filename = filenameMatch[1];
              }
            }
          }
          saveAs(blob, filename); // Use file-saver to download the blob
          this.messageService.add({
            severity: "success",
            summary: "Export Successful",
            detail: "Audit logs exported successfully.",
            life: 3000,
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error("Error during export:", error);
          this.messageService.add({
            severity: "error",
            summary: "Export Failed",
            detail: error.error?.message || "An error occurred during export. Please try again.",
            life: 5000,
          });
        },
      });
  }
}
